// Default color styles

import { FreshpaintConsentManagerConfigurableCssVariableKey } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";

// Reference for css vars here: https://github.com/orestbida/cookieconsent/blob/master/src/scss/abstracts/_dark-color-scheme.scss
export const defaultColorStyles: Record<
  FreshpaintConsentManagerConfigurableCssVariableKey,
  string
> = {
  "--cc-bg": "#161a1c",
  "--cc-primary-color": "#ebf3f6",
  "--cc-secondary-color": "#aebbc5",
  "--cc-btn-primary-bg": "#c2d0e0",
  "--cc-btn-primary-color": "#161a1c",
  "--cc-btn-primary-border-color": "#c2d0e0",
  "--cc-btn-primary-hover-bg": "#98a7b6",
  "--cc-btn-primary-hover-color": "#000000",
  "--cc-btn-primary-hover-border-color": "#98a7b6",
  "--cc-btn-secondary-bg": "#242c31",
  "--cc-btn-secondary-color": "#ebf3f6",
  "--cc-btn-secondary-border-color": "#242c31",
  "--cc-btn-secondary-hover-bg": "#353d43",
  "--cc-btn-secondary-hover-color": "#ffffff",
  "--cc-btn-secondary-hover-border-color": "#353d43",
  "--cc-separator-border-color": "#222a30",
  "--cc-toggle-on-bg": "#c2d0e0",
  "--cc-toggle-off-bg": "#525f6b",
  "--cc-toggle-on-knob-bg": "#ebf3f6",
  "--cc-toggle-off-knob-bg": "#ebf3f6",
  "--cc-toggle-enabled-icon-color": "#ebf3f6",
  "--cc-toggle-disabled-icon-color": "#ebf3f6",
  "--cc-toggle-readonly-bg": "#343e45",
  "--cc-toggle-readonly-knob-bg": "#5f6b72",
  "--cc-toggle-readonly-knob-icon-color": "#343e45",
  "--cc-section-category-border": "#1e2428",
  "--cc-cookie-category-block-bg": "#1e2428",
  "--cc-cookie-category-block-border": "#1e2428",
  "--cc-cookie-category-block-hover-bg": "#242c31",
  "--cc-cookie-category-block-hover-border": "#232a2f",
  "--cc-cookie-category-expanded-block-bg": "transparent",
  "--cc-cookie-category-expanded-block-hover-bg": "#343e45",
  "--cc-overlay-bg": "rgba(0, 0, 0, 0.65)",
  "--cc-webkit-scrollbar-bg": "#1e2428",
  "--cc-webkit-scrollbar-hover-bg": "#98a7b6",
  "--cc-footer-bg": "#0c0e0f",
  "--cc-footer-color": "#aebbc5",
  "--cc-footer-border-color": "#060809",
};

export interface ColorInputConfig {
  label: string;
  cssVar: FreshpaintConsentManagerConfigurableCssVariableKey;
  description?: string;
}

export interface ColorSectionConfig {
  title: string;
  colors: ColorInputConfig[];
  className?: string;
}

export const COLOR_SECTIONS: ColorSectionConfig[] = [
  {
    title: "Basic Colors",
    colors: [
      { label: "Background Color", cssVar: "--cc-bg" },
      { label: "Primary Text", cssVar: "--cc-primary-color" },
      { label: "Secondary Text", cssVar: "--cc-secondary-color" },
      { label: "Separator Border", cssVar: "--cc-separator-border-color" },
    ],
  },
  {
    title: "Primary Buttons (Accept All, Reject All)",
    colors: [
      { label: "Background", cssVar: "--cc-btn-primary-bg" },
      { label: "Text Color", cssVar: "--cc-btn-primary-color" },
      { label: "Border Color", cssVar: "--cc-btn-primary-border-color" },
      { label: "Hover Background", cssVar: "--cc-btn-primary-hover-bg" },
      { label: "Hover Text Color", cssVar: "--cc-btn-primary-hover-color" },
      {
        label: "Hover Border Color",
        cssVar: "--cc-btn-primary-hover-border-color",
      },
    ],
  },
  {
    title: "Secondary Buttons (Manage Preferences, Accept Current Selection)",
    colors: [
      { label: "Background", cssVar: "--cc-btn-secondary-bg" },
      { label: "Text Color", cssVar: "--cc-btn-secondary-color" },
      { label: "Border Color", cssVar: "--cc-btn-secondary-border-color" },
      { label: "Hover Background", cssVar: "--cc-btn-secondary-hover-bg" },
      { label: "Hover Text Color", cssVar: "--cc-btn-secondary-hover-color" },
      {
        label: "Hover Border Color",
        cssVar: "--cc-btn-secondary-hover-border-color",
      },
    ],
  },
  {
    title: "Toggle Switches",
    colors: [
      { label: "Toggle On Background", cssVar: "--cc-toggle-on-bg" },
      { label: "Toggle Off Background", cssVar: "--cc-toggle-off-bg" },
      { label: "Toggle On Knob", cssVar: "--cc-toggle-on-knob-bg" },
      { label: "Toggle Off Knob", cssVar: "--cc-toggle-off-knob-bg" },
      { label: "Enabled Icon Color", cssVar: "--cc-toggle-enabled-icon-color" },
      {
        label: "Disabled Icon Color",
        cssVar: "--cc-toggle-disabled-icon-color",
      },
    ],
  },
  {
    title: "Read-only Toggles (Essential Categories)",
    colors: [
      { label: "Background", cssVar: "--cc-toggle-readonly-bg" },
      { label: "Knob Background", cssVar: "--cc-toggle-readonly-knob-bg" },
      {
        label: "Knob Icon Color",
        cssVar: "--cc-toggle-readonly-knob-icon-color",
      },
    ],
  },
  {
    title: "Category Sections",
    colors: [
      {
        label: "Category Block Background",
        cssVar: "--cc-cookie-category-block-bg",
      },
      {
        label: "Category Block Border",
        cssVar: "--cc-cookie-category-block-border",
      },
      {
        label: "Category Block Hover Bg",
        cssVar: "--cc-cookie-category-block-hover-bg",
      },
      {
        label: "Category Block Hover Border",
        cssVar: "--cc-cookie-category-block-hover-border",
      },
      {
        label: "Expanded Block Hover Bg",
        cssVar: "--cc-cookie-category-expanded-block-hover-bg",
      },
    ],
  },
  {
    title: "Other Elements",
    className: "mb-6 border-b-0",
    colors: [
      { label: "Overlay Background", cssVar: "--cc-overlay-bg" },
      { label: "Scrollbar Background", cssVar: "--cc-webkit-scrollbar-bg" },
      { label: "Scrollbar Hover", cssVar: "--cc-webkit-scrollbar-hover-bg" },
      { label: "Footer Background", cssVar: "--cc-footer-bg" },
      { label: "Footer Text Color", cssVar: "--cc-footer-color" },
      { label: "Footer Border Color", cssVar: "--cc-footer-border-color" },
    ],
  },
];
